// Analytics service for tracking user interactions

let isInitialized = false;

export const initializeAnalytics = () => {
  if (isInitialized || process.env.NODE_ENV !== 'production') {
    return;
  }

  // Initialize Google Analytics 4 if tracking ID is provided
  if (process.env.REACT_APP_GA_TRACKING_ID) {
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GA_TRACKING_ID}`;
    script.async = true;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', process.env.REACT_APP_GA_TRACKING_ID, {
      page_title: 'KosherEats',
      page_location: window.location.href,
    });
  }

  isInitialized = true;
};

// Event tracking functions
export const trackEvent = (eventName, eventData = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Analytics Event:', eventName, eventData);
    return;
  }

  // Google Analytics 4
  if (window.gtag) {
    window.gtag('event', eventName, {
      event_category: eventData.category || 'engagement',
      event_label: eventData.label,
      value: eventData.value,
      custom_map: eventData.customParameters,
      ...eventData
    });
  }
};

// Page view tracking
export const trackPageView = (pagePath, pageTitle) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Analytics Page View:', pagePath, pageTitle);
    return;
  }

  if (window.gtag) {
    window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID, {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

// Specific tracking functions for KosherEats events
export const analytics = {
  // Search tracking
  trackSearch: (query, filters, resultCount) => {
    trackEvent('search', {
      category: 'search',
      search_term: query,
      filters_applied: Object.keys(filters).filter(key => filters[key]).length,
      result_count: resultCount,
      label: `${query} (${resultCount} results)`
    });
  },

  // Restaurant interactions
  trackRestaurantView: (restaurantId, restaurantName, source = 'search') => {
    trackEvent('restaurant_view', {
      category: 'restaurant',
      restaurant_id: restaurantId,
      restaurant_name: restaurantName,
      source: source,
      label: restaurantName
    });
  },

  trackRestaurantContact: (restaurantId, contactMethod) => {
    trackEvent('restaurant_contact', {
      category: 'conversion',
      restaurant_id: restaurantId,
      contact_method: contactMethod, // phone, website, directions
      value: 1
    });
  },

  // Submission tracking
  trackSubmission: (submissionType, success = true) => {
    trackEvent('submission', {
      category: 'engagement',
      submission_type: submissionType, // new_restaurant, correction
      success: success,
      value: success ? 1 : 0
    });
  },

  // Filter usage
  trackFilterUsage: (filterType, filterValue) => {
    trackEvent('filter_usage', {
      category: 'search',
      filter_type: filterType, // certification, dietary, price, etc.
      filter_value: filterValue,
      label: `${filterType}: ${filterValue}`
    });
  },

  // Map interactions
  trackMapInteraction: (action, restaurantId = null) => {
    trackEvent('map_interaction', {
      category: 'map',
      action: action, // zoom, pan, marker_click, cluster_click
      restaurant_id: restaurantId,
      label: action
    });
  },

  // Review interactions
  trackReviewInteraction: (action, restaurantId, reviewSource) => {
    trackEvent('review_interaction', {
      category: 'reviews',
      action: action, // view_more, external_link
      restaurant_id: restaurantId,
      review_source: reviewSource, // google, yelp, yth, jfe
      label: `${action} - ${reviewSource}`
    });
  },

  // User flow tracking
  trackUserFlow: (flowStep, flowType = 'general') => {
    trackEvent('user_flow', {
      category: 'user_experience',
      flow_type: flowType, // search_to_restaurant, home_to_submit
      flow_step: flowStep,
      label: `${flowType}: ${flowStep}`
    });
  },

  // Error tracking
  trackError: (errorType, errorMessage, context = {}) => {
    trackEvent('error', {
      category: 'errors',
      error_type: errorType, // api_error, validation_error, etc.
      error_message: errorMessage.substring(0, 100), // Truncate for privacy
      page: window.location.pathname,
      ...context
    });
  },

  // Performance tracking
  trackPerformance: (metric, value, context = {}) => {
    trackEvent('performance', {
      category: 'performance',
      metric: metric, // page_load_time, api_response_time, etc.
      value: Math.round(value),
      ...context
    });
  },

  // Feature usage
  trackFeatureUsage: (feature, action = 'use') => {
    trackEvent('feature_usage', {
      category: 'features',
      feature: feature, // search_suggestions, auto_location, etc.
      action: action,
      label: feature
    });
  }
};

// Hook for React components to track analytics
export const useAnalytics = () => {
  return {
    trackEvent,
    trackPageView,
    ...analytics
  };
};

export default analytics;